/**
 * NPC lifecycle hooks: passive auras on engage (Septic Bite), damage links (Uncoiled Writhes
 * share damage with the Coil) and on-death effects (Septic Spatter). Also cleans mechanic state
 * owned by / aimed at a unit that died.
 */
import type { EffectCtx, SimContext, Unit, UnitId } from '../../contracts';
import { DAMAGE_LINK_ABILITY_ID, mstate, runEffects, unitOf } from './state';

/** The NPC entered combat (AI enterCombat). */
export function onEngage(ctx: SimContext, npc: Unit): void {
  const def = ctx.content.npc(npc.npcId ?? '');
  if (!def) return;
  for (const aura of def.auras ?? []) {
    if (!npc.auras.some((a) => a.auraId === aura.id)) ctx.sys.combat.applyAura(ctx, npc.id, aura.id, npc.id, aura.id);
  }
  if (def.damageLink) {
    const st = mstate(ctx.world);
    if (!st.links.some((l) => l.unitId === npc.id)) st.links.push({ unitId: npc.id, to: def.damageLink.to, pct: def.damageLink.pct, lastHp: npc.hp });
  }
}

function linkTarget(ctx: SimContext, u: Unit, to: 'summoner' | 'boss'): Unit | undefined {
  if (to === 'summoner') return unitOf(ctx, u.summonerId);
  const enc = ctx.world.encounter;
  if (!enc) return unitOf(ctx, u.summonerId);
  for (const id of enc.bossIds) {
    const b = ctx.world.units[id];
    if (b?.alive) return b;
  }
  return undefined;
}

/** Mirror `pct` of the HP each linked unit lost since last tick onto its link target. */
export function updateLinks(ctx: SimContext): void {
  const st = ctx.world.mechanics;
  if (!st || st.links.length === 0) return;
  st.links = st.links.filter((l) => {
    const u = ctx.world.units[l.unitId];
    if (!u) return false;
    const lost = l.lastHp - Math.max(0, u.hp);
    l.lastHp = Math.max(0, u.hp);
    const t = linkTarget(ctx, u, l.to);
    if (lost > 0 && t?.alive && t.id !== u.id) {
      ctx.sys.combat.dealDamage(ctx, {
        sourceId: u.id,
        targetId: t.id,
        abilityId: DAMAGE_LINK_ABILITY_ID,
        school: 'nature',
        amount: Math.round(lost * l.pct),
        preScaled: true,
        canCrit: false,
        avoidable: false,
      });
    }
    return u.alive;
  });
}

/** A unit died (AI onDeath): on-death effects and mechanic cleanup. */
export function onUnitDeath(ctx: SimContext, unitId: UnitId): void {
  const u = ctx.world.units[unitId];
  if (!u) return;
  // Settle the final hit for damage links before the unit drops off.
  updateLinks(ctx);
  const def = u.npcId ? ctx.content.npc(u.npcId) : undefined;
  if (def?.onDeath?.length) {
    const ectx: EffectCtx = { sourceId: u.id, abilityId: `${def.id}.on_death`, targetId: u.id, point: { x: u.pos.x, y: u.pos.y }, depth: 1 };
    runEffects(ctx, def.onDeath, ectx);
  }
  const st = ctx.world.mechanics;
  if (!st) return;
  // Fixates on the dead unit end; mechanics sourced by it are dropped on their next update.
  for (const id of ctx.world.unitOrder) {
    const t = ctx.world.units[id]?.threat;
    if (t?.fixate?.on === unitId) t.fixate = null;
  }
}
